import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './LogPage.css';
import { deleteWorkLog, getWorkLogs, updateWorkLogMemo } from '../api';
import useMachineryWebSocket from '../hooks/useMachineryWebSocket';
import { AuthContext } from '../context/AuthContext';
import { MaterialTypesContext } from '../context/MaterialTypesContext';
import { formatMaterialSize, formatMeasurement } from '../materials/materialSize';
import Pagination from '../components/Pagination/Pagination';

const PAGE_SIZE = 20;

const getStatusInfo = (status) => {
  switch (status) {
    case '완료': return { text: '완료', className: 'status-bar-completed' };
    case '알람': return { text: '알람', className: 'status-bar-alarm' };
    case '중단': return { text: '중단', className: 'status-bar-stopped' };
    default: return null;
  }
};

const formatTime = (value) => value && value !== '0001-01-01T00:00:00Z'
  ? new Date(value).toLocaleString('ko-KR', {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  })
  : '-';

function LogGroupCard({ record, isAdmin, onMemoSaved, onDelete }) {
  const materialContext = useContext(MaterialTypesContext) || {};
  const resolveMaterialType = materialContext.resolveMaterialType || ((value, fallback) => fallback || value || '-');
  const resolveMaterialShape = materialContext.resolveMaterialShape || ((value, fallback) => fallback || value || '-');
  const [isEditing, setIsEditing] = useState(false);
  const [memo, setMemo] = useState(record.memo || '');
  const [draft, setDraft] = useState(record.memo || '');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const statusInfo = getStatusInfo(record.status);
  if (!statusInfo) return null;

  const resolvedMaterialShape = resolveMaterialShape(record.materialShapeCode ?? record.materialShape, record.materialShape);
  const materialText = `${resolveMaterialType(record.materialTypeCode ?? record.materialType, record.materialType)} / ${resolvedMaterialShape}`;
  const inputLengthText = formatMeasurement(record.inputLength);
  const saveMemo = async () => {
    setIsSaving(true);
    setError('');
    try {
      const updated = await updateWorkLogMemo(record.id, draft);
      const nextMemo = updated.memo ?? draft.trim();
      setMemo(nextMemo);
      setDraft(nextMemo);
      setIsEditing(false);
      onMemoSaved(record.id, nextMemo);
    } catch (requestError) {
      setError(requestError.message || '메모 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="log-group-card">
      <div className="log-summary-row">
        <div className={`log-status-sidebar ${statusInfo.className}`}>{statusInfo.text}</div>
        <div className="log-summary-content">
          <div className="log-summary-grid">
            <div className="ls-cell highlight">{record.machineName}</div>
            <div className="ls-cell bold">{record.jobName}</div>
            <div className="ls-cell">{materialText}</div>
            <div className="ls-cell">{formatMaterialSize(
              record.materialShapeCode, resolvedMaterialShape, record.materialWidth, record.materialHeight,
            )} mm</div>
            <div className="ls-cell">{inputLengthText === '-' ? '-' : `${inputLengthText} mm`}</div>
            <div className="ls-cell count-highlight">{record.workNum ?? 0} / {record.inputNum ?? 0}</div>
            <div className="ls-cell time">{formatTime(record.createdAt)}</div>
            <div className="ls-cell log-message-cell">{record.message || '-'}</div>
            <div className="ls-cell log-card-actions">
              {isAdmin && <button type="button" onClick={() => setIsEditing(true)}>메모</button>}
              {isAdmin && <button type="button" className="delete-work-log-btn" onClick={() => onDelete(record)}>삭제</button>}
            </div>
          </div>
        </div>
      </div>
      {!isEditing && memo && <div className="log-memo-readonly">{memo}</div>}
      {isEditing && (
        <div className="log-memo-editor">
          <input
            aria-label="작업 기록 메모"
            type="text"
            value={draft}
            onChange={(event) => {
              if (Array.from(event.target.value).length <= 100) setDraft(event.target.value);
            }}
          />
          <span>{Array.from(draft).length}/100</span>
          <button type="button" disabled={isSaving} onClick={saveMemo}>저장</button>
          <button type="button" disabled={isSaving} onClick={() => { setDraft(memo); setError(''); setIsEditing(false); }}>취소</button>
          {error && <span className="log-action-error" role="alert">{error}</span>}
        </div>
      )}
    </div>
  );
}

function LogPage() {
  const [records, setRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [machineName, setMachineName] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { companyID } = useParams();
  const auth = useContext(AuthContext);
  const isAdmin = auth?.user?.role === 'company_admin';
  const lastMessage = useMachineryWebSocket();
  const [refreshVersion, setRefreshVersion] = useState(0);

  const getTodayString = () => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const today = getTodayString();
  const [startDate, setStartDate] = useState(() => sessionStorage.getItem('logStartDate') || today);
  const [endDate, setEndDate] = useState(() => sessionStorage.getItem('logEndDate') || today);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const updateStartDate = (value) => {
    sessionStorage.setItem('logStartDate', value);
    setStartDate(value);
    setPage(1);
    setRecords([]);
  };

  const updateEndDate = (value) => {
    sessionStorage.setItem('logEndDate', value);
    setEndDate(value);
    setPage(1);
    setRecords([]);
  };

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    getWorkLogs({
      search: searchTerm, machineName, status: statusFilter,
      startDate, endDate, page, pageSize: PAGE_SIZE,
    }).then((result) => {
      if (!active) return;

      if (Array.isArray(result)) {
        setRecords(result.filter((record) => getStatusInfo(record.status)));
        setTotalItems(result.length);
        setTotalPages(result.length > 0 ? page : 0);
        setIsLoading(false);
        return;
      }

      const items = Array.isArray(result?.items) ? result.items : [];
      const pagination = result?.pagination || {};
      const nextTotalItems = Number.isFinite(pagination.totalItems) && pagination.totalItems > 0
        ? Math.floor(pagination.totalItems) : 0;
      const nextTotalPages = Number.isFinite(pagination.totalPages) && pagination.totalPages > 0
        ? Math.floor(pagination.totalPages) : 0;
      const lastValidPage = Math.max(1, nextTotalPages);
      if (page > lastValidPage) {
        setPage(lastValidPage);
        return;
      }

      setRecords(items.filter((record) => getStatusInfo(record.status)));
      setTotalItems(nextTotalItems);
      setTotalPages(nextTotalPages);
      setIsLoading(false);
    }).catch((error) => {
      if (active) {
        console.error(error);
        setRecords([]);
        setTotalItems(0);
        setTotalPages(0);
        setIsLoading(false);
      }
    });
    return () => { active = false; };
  }, [companyID, searchTerm, machineName, statusFilter, startDate, endDate, page, refreshVersion]);

  useEffect(() => {
    if (lastMessage && lastMessage.type === 'WORK_ORDER_UPDATE') {
      setRefreshVersion((version) => version + 1);
    }
  }, [lastMessage]);

  const resetFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  const deleteRecord = async (record) => {
    if (!window.confirm(`'${record.jobName}' 작업 기록을 삭제하시겠습니까?`)) return;
    try {
      await deleteWorkLog(record.id);
      setRefreshVersion((version) => version + 1);
    } catch (error) {
      window.alert(error.message || '작업 기록 삭제에 실패했습니다.');
    }
  };

  return (
    <div className="log-page-background">
      <div className="log-main-container">
        <div className="log-title-bar">
          <h1>작 업 기 록</h1>
          <div className="log-controls">
            <div className="date-filter-group">
              <input type="date" value={startDate} onChange={(e) => updateStartDate(e.target.value)} className="log-date-input" />
              <span>~</span>
              <input type="date" value={endDate} onChange={(e) => updateEndDate(e.target.value)} className="log-date-input" />
            </div>
            <select aria-label="상태 필터" className="log-filter-select" value={statusFilter} onChange={resetFilter(setStatusFilter)}>
              <option value="all">전체 (All)</option>
              <option value="완료">완료 (Completed)</option>
              <option value="알람">알람 (Alarm)</option>
              <option value="중단">중단 (Stopped)</option>
            </select>
            <input aria-label="설비명 필터" type="text" placeholder="설비명 (정확히)" className="log-search-input" value={machineName} onChange={resetFilter(setMachineName)} />
            <input aria-label="작업명 검색" type="text" placeholder="작업명 검색" className="log-search-input" value={searchTerm} onChange={resetFilter(setSearchTerm)} />
          </div>
        </div>
        <div className="log-list-wrapper">
          <div className="log-list-header-row">
              <div className="log-header-sidebar-box">상태</div>
              <div className="log-header-content-box">
                  <div className="log-summary-grid header">
                      <div>설비명</div><div>작업명</div><div>소재</div><div>크기</div><div>길이</div><div>수량(실적/지시)</div><div>기록 시간</div><div>메시지</div><div>작업</div>
                  </div>
              </div>
          </div>
          {isLoading ? <div className="loading-msg">로딩 중...</div> : records.length > 0 ? records.map((record) => (
            <LogGroupCard
              key={record.id}
              record={record}
              isAdmin={isAdmin}
              onMemoSaved={(id, memo) => setRecords((current) => current.map((item) => item.id === id ? { ...item, memo } : item))}
              onDelete={deleteRecord}
            />
          )) : <div className="no-data-msg">기록 없음</div>}
        </div>
        <Pagination page={page} totalPages={totalPages} totalItems={totalItems} onPageChange={setPage} />
      </div>
    </div>
  );
}

export default LogPage;
